import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Image as ImageIcon, X } from 'lucide-react';
import BaseForm from './BaseForm';
import { Checkbox } from '../ui/checkbox';

const PartnershipForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false
}) => {
  const [previewUrl, setPreviewUrl] = useState(() => {
    return formData.logoUrl || '';
  });

  useEffect(() => {
    if (!formData.logoUrl) {
      setPreviewUrl('');
      return;
    }

    if (formData.logoUrl.startsWith('data:')) {
      setPreviewUrl(formData.logoUrl);
      return;
    }

    setPreviewUrl(formData.logoUrl);
  }, [formData.logoUrl]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
        e.target.value = '';
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('Image size should be less than 5MB');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setFormData(prev => ({
          ...prev,
          logoFile: file,
          logoUrl: reader.result
        }));
      };
      reader.onerror = () => {
        console.error('Error reading file');
        toast.error('Error reading file. Please try another image.');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      logoFile: null,
      logoUrl: ''
    }));
    const fileInput = document.getElementById('logoFile');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.partnerName?.trim()) {
      toast.error('Partner name is required');
      return false;
    }
    if (formData.partnerName.length > 300) {
      toast.error('Partner name must be 300 characters or less');
      return false;
    }
    const hasType = Array.isArray(formData.type)
      ? formData.type.length > 0
      : !!formData.type;
    if (!hasType) {
      toast.error('Please select at least one partnership type');
      return false;
    }
    if (!formData.country?.trim()) {
      toast.error('Country is required');
      return false;
    }
    if (!formData.description?.trim()) {
      toast.error('Description is required');
      return false;
    }
    if (formData.description.length > 100) {
      toast.error('Description must be 100 characters or less');
      return false;
    }
    if (!formData.details?.trim()) {
      toast.error('Details are required');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <BaseForm
      title={isEdit ? 'Edit Partnership' : 'Add Partnership'}
      description="Add or update partnership details"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitting={submitting}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="partnerName" className="block text-sm font-medium mb-1">Partner Name *</label>
          <Input
            id="partnerName"
            value={formData.partnerName || ''}
            onChange={handleChange}
            placeholder="Partner organization name"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Partnership Type *</label>
            <div className="flex flex-wrap gap-4 p-3 border rounded-md bg-white">
              {['Strategic', 'Research', 'Dual Degree', 'Student Exchange'].map((ptype) => {
                const isChecked = Array.isArray(formData.type)
                  ? formData.type.includes(ptype)
                  : formData.type === ptype;

                return (
                  <label key={ptype} className="flex items-center space-x-2 text-sm font-medium cursor-pointer">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFormData(prev => {
                          const currentTypes = Array.isArray(prev.type)
                            ? prev.type
                            : prev.type
                              ? [prev.type]
                              : [];

                          let newTypes;
                          if (checked) {
                            newTypes = [...currentTypes, ptype];
                          } else {
                            newTypes = currentTypes.filter(t => t !== ptype);
                          }

                          return {
                            ...prev,
                            type: newTypes
                          };
                        });
                      }}
                    />
                    <span>{ptype}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium mb-1">Country *</label>
            <Input
              id="country"
              value={formData.country || ''}
              onChange={handleChange}
              placeholder="e.g., United States"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-1">Short Description * (max 100 chars)</label>
          <Textarea
            id="description"
            value={formData.description || ''}
            onChange={handleChange}
            placeholder="Brief description (max 100 characters)"
            rows={2}
            maxLength={100}
            required
          />
          <div className="flex justify-end mt-1">
            <div className="text-xs text-gray-500">
              {formData.description?.length || 0}/100 characters
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="details" className="block text-sm font-medium mb-1">Detailed Information *</label>
          <Textarea
            id="details"
            value={formData.details || ''}
            onChange={handleChange}
            placeholder="Detailed partnership information"
            rows={4}
            required
          />
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium mb-1">Website</label>
          <Input
            id="website"
            type="url"
            value={formData.website || ''}
            onChange={handleChange}
            placeholder="https://partner-website.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Partner Logo</label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              {previewUrl ? (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Logo Preview"
                    className="max-h-32 mx-auto rounded-md object-contain"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="mt-2 text-xs text-green-600">Logo loaded - Click X to remove</div>
                </div>
              ) : (
                <>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="logoFile"
                      className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-3 py-2 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      <span>Upload a file</span>
                      <input
                        id="logoFile"
                        name="logoFile"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF, SVG up to 5MB</p>
                  {isEdit && !previewUrl && !formData.logoUrl && (
                    <div className="mt-2 text-xs text-orange-600 font-semibold">No logo - upload one to add</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  );
};

export default PartnershipForm;